import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  CheckCircle2,
  Clock,
  Star,
  Users,
  CalendarClock,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { reportsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Loader2 } from 'lucide-react';

interface OverviewDashboardProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface StatItem {
  label: string;
  value: string | number;
  change?: number;
  icon: React.ReactElement;
  format?: 'number' | 'percentage' | 'time' | 'rating';
}

const formatValue = (val: string | number, format: StatItem['format'] = 'number') => {
  if (format === 'percentage') return `${val}%`;
  if (format === 'time') return `${val} min`;
  if (format === 'rating') return `${val}/5.0`;
  if (format === 'number' && typeof val === 'number') return val.toLocaleString();
  return val;
};

export const OverviewDashboard: React.FC<OverviewDashboardProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [conversationReport, setConversationReport] = useState<any>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboard, conversations] = await Promise.all([
        reportsAPI.getDashboardMetrics(),
        reportsAPI.getConversationReport({
          start_date: dateRange.startDate.toISOString().split('T')[0],
          end_date: dateRange.endDate.toISOString().split('T')[0],
        }),
      ]);

      setDashboardData(dashboard);
      setConversationReport(conversations);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!dashboardData || !conversationReport) {
    return (
      <Alert className="m-2">
        <AlertDescription>No data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const stats: StatItem[] = [
    {
      label: 'Total Conversations',
      value: dashboardData.total_conversations || conversationReport.total_conversations,
      change: dashboardData.changes?.conversations,
      icon: <MessageSquare />,
    },
    {
      label: 'Resolution Rate',
      value:
        dashboardData.resolution_rate ||
        Math.round(
          (conversationReport.resolved_conversations / conversationReport.total_conversations) * 100
        ),
      change: dashboardData.changes?.resolution_rate,
      icon: <CheckCircle2 />,
      format: 'percentage',
    },
    {
      label: 'Avg First Response',
      value:
        dashboardData.avg_first_response_time ||
        Math.round(conversationReport.avg_first_response_time),
      change: dashboardData.changes?.first_response_time,
      icon: <Clock />,
      format: 'time',
    },
    {
      label: 'CSAT Score',
      value: dashboardData.avg_csat_30_days,
      change: dashboardData.changes?.csat,
      icon: <Star />,
      format: 'rating',
    },
    {
      label: 'Open',
      value: dashboardData.open_conversations,
      icon: <CalendarClock />,
    },
    {
      label: 'Pending',
      value: dashboardData.pending_conversations,
      icon: <Users />,
    },
  ];

  const channelTotal = Object.values(conversationReport.conversations_by_channel).reduce<number>(
    (sum, c) => sum + (c as number),
    0,
  );

  const statusTotal = Object.values(conversationReport.conversations_by_status).reduce<number>(
    (sum, c) => sum + (c as number),
    0,
  );

  return (
    <div className="space-y-5">

      {/* ── Inline stat strip — replaces 6 isolated metric cards ─────────
           All key numbers in one scannable horizontal row.
           Dividers create visual separation without box chrome.          */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">
        {stats.map((stat, index) => {
          const TrendIcon =
            stat.change !== undefined
              ? stat.change > 0
                ? TrendingUp
                : stat.change < 0
                ? TrendingDown
                : Minus
              : null;

          const changeColor =
            stat.change !== undefined
              ? stat.change > 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : stat.change < 0
                ? 'text-red-500 dark:text-red-400'
                : 'text-muted-foreground'
              : '';

          return (
            <div key={index} className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
              {/* Label */}
              <span className="text-xs text-muted-foreground leading-none truncate">
                {stat.label}
              </span>
              {/* Value */}
              <span className="text-lg font-semibold tabular-nums leading-snug mt-1">
                {formatValue(stat.value, stat.format)}
              </span>
              {/* Trend badge */}
              {stat.change !== undefined && TrendIcon && (
                <span className={cn('flex items-center gap-0.5 text-xs font-medium mt-0.5', changeColor)}>
                  <TrendIcon className="h-3 w-3 shrink-0" />
                  {stat.change > 0 ? '+' : ''}
                  {stat.change.toFixed(1)}%
                  <span className="font-normal text-muted-foreground ml-1">vs prev</span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Channel + Status breakdown ─────────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-2">

        {/* Conversations by Channel */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Conversations by Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(conversationReport.conversations_by_channel).map(
                ([channel, count]) => {
                  const pct = channelTotal > 0 ? ((count as number) / channelTotal) * 100 : 0;
                  return (
                    <div key={channel} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm capitalize">{channel}</span>
                        <span className="text-sm font-medium tabular-nums">{count as number}</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                },
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(conversationReport.conversations_by_status).map(
                ([status, count]) => {
                  const pct = statusTotal > 0 ? ((count as number) / statusTotal) * 100 : 0;
                  return (
                    <div key={status} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm capitalize">{status}</span>
                        <span className="text-sm font-medium tabular-nums">{count as number}</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                },
              )}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};
